import * as api from "../../api";
import * as adminAPI from "../../api/admin.js";
import { switchToPublicEndpoints } from "../../api/index.js";
import { deactivatePushNotification, getOnboardSteps } from "../../api/settings.js";
import { setEnvironmentOverride, clearEnvironmentOverride } from "../../config/index.js";

export const updatePassword = (password, token) => {
  return async (dispatch) => {
    const result = await api.updatePassword(token, password);
    localStorage.setItem("token", result.token);
    dispatch({ type: "LOGIN_SUCCESS", payload: result });
    return result;
  };
}
export const login = (email, password, mfaCode = null) => {
  return async (dispatch) => {
    try {
      // Check if this is a demo email and set environment override
      const demoEmails = ['demo@saucey.com', 'demo@sauceydirect.com'];
      const isDemoUser = demoEmails.includes(email.toLowerCase());
      
      if (isDemoUser) {
        setEnvironmentOverride('development');
        // API instances will now read from the overridden config
        // Switch to public endpoints to pick up the new dev config
        switchToPublicEndpoints();
      }else{
        clearEnvironmentOverride();
        switchToPublicEndpoints();
      }
      
      const result = await api.login(email, password, mfaCode);
      // Check if MFA code is required
      if (result.f === "mfa") {
        return { requiresMFA: true, email, password, offer: result.offer };
      }
      
      // Check if this is an admin user (VPN logic is now handled in the API)
      const isAdmin = result.user?.type === "saucey_admin";
      
      if (isAdmin) {
        dispatch({ type: "SET_VPN_REQUIRED", payload: true });
        dispatch({ type: "SET_VPN_CONNECTED", payload: true });
      }
      
      // Check if MFA setup is required
      if (result.f === "setup_mfa") {
        localStorage.setItem("token", result.token);
        dispatch({ type: "SET_MFA_SETUP_REQUIRED", payload: true });
        // Don't set as fully authenticated until MFA is set up
        dispatch({ type: "LOGIN_PARTIAL_SUCCESS", payload: result });
        return result;
      }
      
      localStorage.setItem("token", result.token);
      dispatch({ type: "LOGIN_SUCCESS", payload: result });
      
      // Check for conversion_job_id and set it in Redux state, or clear it if not present
      const conversionJobId = result.conversion_job_id || result.data?.conversion_job_id;
      dispatch({ type: "SET_PROSPECT_CONVERSION_ID", payload: conversionJobId || null });
      
      // Set demo user state and show modal if this is a demo user
      dispatch({ type: "SET_IS_DEMO_USER", payload: isDemoUser });
      if (isDemoUser) {
        dispatch({ type: "SET_DEMO_MODAL_SHOW", payload: true });
      }
      
      return result;
    } catch (error) {
      // Handle different types of errors
      if (error.name === 'HTTPError' && error.response?.status === 401) {
        throw new Error("Invalid credentials. Please try again.");
      } else if (error.message) {
        // Re-throw custom errors (like VPN errors) with their original message
        throw error;
      } else {
        // Handle other types of errors
        throw new Error("Login failed. Please try again.");
      }
    }
  };
};

export const loginWithWebAuthn = (email, password, credential) => {
  return async (dispatch) => {
    try {
      // Check if this is a demo email and set environment override
      const demoEmails = ['demo@saucey.com', 'demo@sauceydirect.com'];
      const isDemoUser = demoEmails.includes(email.toLowerCase());
      
      if (isDemoUser) {
        setEnvironmentOverride('development');
        // API instances will now read from the overridden config
        // Switch to public endpoints to pick up preserved dev config
        switchToPublicEndpoints();
      }
      
      const result = await api.loginWithWebAuthn(email, password, credential);
      
      // Check if this is an admin user (VPN logic is now handled in the API)
      const isAdmin = result.user?.type === "saucey_admin";
      
      if (isAdmin) {
        dispatch({ type: "SET_VPN_REQUIRED", payload: true });
        dispatch({ type: "SET_VPN_CONNECTED", payload: true });
      }
      
      // Check if MFA setup is required
      if (result.f === "setup_mfa") {
        localStorage.setItem("token", result.token);
        dispatch({ type: "SET_MFA_SETUP_REQUIRED", payload: true });
        // Don't set as fully authenticated until MFA is set up
        dispatch({ type: "LOGIN_PARTIAL_SUCCESS", payload: result });
        return result;
      }
      
      localStorage.setItem("token", result.token);
      dispatch({ type: "LOGIN_SUCCESS", payload: result });
      
      // Check for conversion_job_id and set it in Redux state, or clear it if not present
      const conversionJobId = result.conversion_job_id || result.data?.conversion_job_id;
      dispatch({ type: "SET_PROSPECT_CONVERSION_ID", payload: conversionJobId || null });
      
      // Set demo user state and show modal if this is a demo user
      dispatch({ type: "SET_IS_DEMO_USER", payload: isDemoUser });
      if (isDemoUser) {
        dispatch({ type: "SET_DEMO_MODAL_SHOW", payload: true });
      }
      
      return result;
    } catch (error) {
      // Handle different types of errors
      if (error.name === 'HTTPError' && error.response?.status === 401) {
        throw new Error("Invalid credentials or passkey. Please try again.");
      } else if (error.message) {
        // Re-throw custom errors (like VPN errors) with their original message
        throw error;
      } else {
        // Handle other types of errors
        throw new Error("Login failed. Please try again.");
      }
    }
  };
};

export const getDashboardActions = () => {
  return async (dispatch) => {
    const result = await adminAPI.getDashboardActions();
    dispatch({
      type: "SET_DASHBOARD_ACTION_COUNTS",
      payload: {
        idScanReview: Number(result["id-scan-review"]) || 0,
        prospect: Number(result.prospect) || 0,
      },
    });
  };
};

export const getIDScans = () => {
  return async (dispatch) => {
    const result = await adminAPI.getIDScans();
    dispatch({ type: "SET_ID_SCANS", payload: result.scans });
  };
};
export const addToast = (toast) => {
  return async (dispatch) => {
    if (!toast.id) {
      toast.id = Math.random();
    }
    if (toast.timer) {
      setTimeout(() => {
        dispatch({ type: "REMOVE_TOAST", payload: toast.id });
      }, toast.timer * 1000);
    }
    return dispatch({ type: "ADD_TOAST", payload: toast });
  };
};
export const removeAllToasts = () => {
  return { type: "REMOVE_ALL_TOAST" };
};

export const checkSession = () => {
  return async (dispatch) => {
    try {
      const result = await api.checkSession();
      
      // If checkSession returns null, it means the session is invalid
      if (!result) {
        localStorage.removeItem("token"); // This will trigger storage event for all tabs
        return;
      }
      if(result?.prospect){
        result.user = result.prospect;
      }
      
      // Check if this is a demo user by checking email
      const demoEmails = ['demo@saucey.com', 'demo@sauceydirect.com'];
      const isDemoUser = result.user?.email && demoEmails.includes(result.user.email.toLowerCase());
      
      // Set demo user state
      dispatch({ type: "SET_IS_DEMO_USER", payload: isDemoUser });
      
      // Check if this is an admin user (VPN logic is now handled in the API)
      const isAdmin = result.user?.type === "saucey_admin";
      
      if (isAdmin) {
        dispatch({ type: "SET_VPN_REQUIRED", payload: true });
        dispatch({ type: "SET_VPN_CONNECTED", payload: true });
      }
      
      // Check if MFA setup is required
      if (result.f === "setup_mfa") {
        dispatch({ type: "SET_MFA_SETUP_REQUIRED", payload: true });
        // Don't set as fully authenticated until MFA is set up
        dispatch({ type: "LOGIN_PARTIAL_SUCCESS", payload: result });
      } else {
        dispatch({ type: "LOGIN_SUCCESS", payload: result });
      }
      
      // Check for conversion_job_id and set it in Redux state, or clear it if not present
      const conversionJobId = result.conversion_job_id || result.data?.conversion_job_id;
      dispatch({ type: "SET_PROSPECT_CONVERSION_ID", payload: conversionJobId || null });
    } catch (e) {
      localStorage.removeItem("token"); // This will trigger storage event for all tabs
    }
  };
};

export const logout = (dnr = false) => {
  return async (dispatch) => {
    try {
      // Get user ID and push token before clearing localStorage
      const userId = localStorage.getItem('userId');
      const pushToken = localStorage.getItem('push_token');
      
      // Call deactivate push notification API if we have both user ID and push token
      if (userId && pushToken) {
        try {
          await deactivatePushNotification({
            user_id: userId,
            push_token: pushToken
          });
        } catch (error) {
          // Don't throw error - logout should continue even if deactivation fails
        }
      }
    } catch (error) {
    } finally {
      // Always proceed with logout regardless of deactivation success/failure
      localStorage.removeItem("token");
      localStorage.removeItem("userId");
      localStorage.removeItem("push_token");
      // Clear environment override and switch back to public endpoints
      clearEnvironmentOverride();
      switchToPublicEndpoints();
      dispatch({ type: "LOGOUT" });
      if(!dnr){
        window.location.href = "/";
      }
    }
  };
};

export const getMerchants = () => {
  return async (dispatch) => {
    dispatch({ type: "SET_MERCHANTS_LOAD_STATE", payload: "loading" });
    dispatch({ type: "REMOVE_TOAST", payload: "merchants-load-error" });
    try {
      const result = await api.getMerchants();
      if (result?.merchants?.length > 0) {
        dispatch({ type: "SET_MERCHANTS", payload: result.merchants });
      } else {
        // Request succeeded but no merchants are associated with this account.
        dispatch({ type: "SET_MERCHANTS_LOAD_STATE", payload: "empty" });
      }
    } catch (error) {
      // Surface the failure instead of leaving the loading screen spinning forever.
      dispatch({ type: "SET_MERCHANTS_LOAD_STATE", payload: "error" });
      dispatch(
        addToast({
          type: "error",
          message:
            "Couldn't load your merchants. Check your connection/VPN, then retry.",
          stuck: true,
          id: "merchants-load-error",
        })
      );
    }
  };
};
export const setSelectedMerchant = (id) => {
  return async (dispatch, getState) => {
    let merchant = getState().app.merchants.find((merchant) => merchant.id === id)
    if(!merchant){
      const merchantRes = await adminAPI.getMerchantByID(id)
      merchant = merchantRes.merchant
    }
    if(!merchant){
      dispatch({ type: "ADD_TOAST", payload: { type: "error", message: "Merchant not found" } })
      return;
    }
    dispatch({ type: "SET_SELECTED_MERCHANT", payload: merchant });
  };
}

export const fetchOnboardSteps = () => {
  return async (dispatch) => {
    try {
      const response = await getOnboardSteps();
      dispatch({ type: "SET_ONBOARD_STEPS", payload: response.data || response });
    } catch (error) {
      console.error('Error fetching onboard steps:', error);
      // Set to null on error to clear any stale data
      dispatch({ type: "SET_ONBOARD_STEPS", payload: null });
    }
  };
};
